import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Search, X, Package } from 'lucide-react';
import { API } from '../config';

const playfair = "'Playfair Display', serif";

export const SearchModal = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  // Live debounced search
  useEffect(() => {
    if (!open) return;
    if (q.trim().length === 0) { setResults([]); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/products?search=${encodeURIComponent(q.trim())}`);
        setResults(data || []);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 220);
    return () => clearTimeout(t);
  }, [q, open]);

  // Reset on open
  useEffect(() => { if (open) { setQ(''); setResults([]); } }, [open]);

  // ESC to close
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    if (open) document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-start justify-center pt-20 px-4"
          style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(12px)' }}
          onClick={onClose}
          data-testid="search-modal"
        >
          <motion.div
            initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
            className="w-full max-w-2xl rounded-2xl p-1 overflow-hidden"
            style={{ background: '#0c1f17', border: '1px solid rgba(212,175,55,0.3)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 px-4 py-3 rounded-2xl" style={{ background: 'rgba(0,0,0,0.3)' }}>
              <Search className="w-5 h-5" style={{ color: '#D4AF37' }} />
              <input
                autoFocus value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Cari produk, brand, atau SKU..."
                className="flex-1 bg-transparent outline-none text-lg"
                style={{ color: '#F8FAF9', fontFamily: playfair }}
                data-testid="search-modal-input"
              />
              <button onClick={onClose} style={{ color: '#A3B8AC' }} data-testid="search-modal-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="max-h-[60vh] overflow-y-auto p-2" data-testid="search-modal-results">
              {q.trim().length === 0 ? (
                <p className="px-6 py-12 text-center text-sm" style={{ color: '#A3B8AC' }}>
                  Mulai mengetik untuk mencari produk berdasarkan nama, brand, atau SKU.
                </p>
              ) : loading ? (
                <p className="px-6 py-12 text-center text-sm" style={{ color: '#A3B8AC' }}>Mencari...</p>
              ) : results.length === 0 ? (
                <p className="px-6 py-12 text-center text-sm" style={{ color: '#A3B8AC' }}>
                  Tidak ada produk yang cocok dengan "{q}".
                </p>
              ) : (
                <div className="space-y-1.5">
                  {results.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => { navigate(`/product/${p.id}`); onClose(); }}
                      className="w-full flex items-center gap-3 p-2.5 rounded-xl hover:bg-white/5 transition text-left"
                      data-testid={`search-result-${p.id}`}
                    >
                      {p.images?.[0] || p.image_url ? (
                        <img src={p.images?.[0] || p.image_url} alt={p.name} className="w-14 h-14 rounded-lg object-cover" />
                      ) : (
                        <div className="w-14 h-14 rounded-lg flex items-center justify-center" style={{ background: 'rgba(212,175,55,0.15)' }}>
                          <Package className="w-5 h-5" style={{ color: '#D4AF37' }} />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate" style={{ color: '#F8FAF9' }}>{p.name}</p>
                        <p className="text-xs truncate" style={{ color: '#A3B8AC' }}>{p.category} · {p.sku || '—'}</p>
                      </div>
                      <span className="text-sm font-semibold whitespace-nowrap" style={{ color: '#D4AF37' }}>
                        Rp {new Intl.NumberFormat('id-ID').format(Number(p.price) || 0)}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
